import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { assignableRoles, canManageMember } from "@sp/shared/roles";
import type { Role } from "@sp/shared/enums";
import { useMe } from "../../app/session";
import { useWorkspace } from "../../app/workspace";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { Card, CardHeader } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/Dialog";
import { ErrorState } from "../../components/ui/ErrorState";
import { Field, Input, Select } from "../../components/ui/Field";
import { Loading } from "../../components/ui/Page";
import { Pill } from "../../components/ui/Pill";
import { SrOnly, Table, Td, Th } from "../../components/ui/Table";
import { useAction } from "../../hooks/useAction";
import { useResource } from "../../hooks/useResource";
import { membersApi } from "../../lib/api/members";
import type { Member } from "../../lib/api/types";
import { formatDate } from "../../lib/format";

const ROLE_HINT: Record<Role, string> = {
  owner: "everything, including deleting the workspace",
  admin: "everything except deleting the workspace",
  editor: "videos and streams",
  viewer: "can look, can't change anything",
};

function InviteForm({ workspaceId, roles, onInvited }: { workspaceId: string; roles: Role[]; onInvited: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(roles.includes("editor") ? "editor" : roles[0]!);
  const invite = useAction(async () => { await membersApi.invite(workspaceId, email.trim(), role); setEmail(""); onInvited(); });
  const submit = (e: FormEvent) => { e.preventDefault(); void invite.run(); };
  return (
    <form onSubmit={submit} className="flex flex-col gap-3 border-t-2 border-ink p-5">
      <div className="grid items-end gap-4 md:grid-cols-[1fr_12rem_auto]">
        <Field label="invite by email">
          {({ id }) => <Input id={id} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@example.com" />}
        </Field>
        <Field label="role" hint={ROLE_HINT[role]}>
          {({ id, describedBy }) => (
            <Select id={id} value={role} aria-describedby={describedBy} onChange={(e) => setRole(e.target.value as Role)}>
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          )}
        </Field>
        <Button type="submit" variant="primary" pending={invite.pending} pendingLabel="sending…">send invite</Button>
      </div>
      {invite.error && <Banner tone="bad">{invite.error}</Banner>}
    </form>
  );
}

export function MembersCard() {
  const me = useMe();
  const { workspace, can, writable } = useWorkspace();
  const full = useResource((signal) => membersApi.workspace(workspace.id, signal), [workspace.id]);
  const [removing, setRemoving] = useState<Member | null>(null);
  const act = useAction(async (f: () => Promise<unknown>) => { await f(); await full.reload(); });
  const roles = assignableRoles(workspace.role);
  const manage = can("member.manage");

  if (full.error !== undefined) return <ErrorState error={full.error} onRetry={() => void full.reload()} />;
  if (!full.data) return <Loading />;
  const pending = full.data.invitations.filter((i) => i.status === "pending");
  const seats = workspace.plan?.maxMembers ?? 0;
  const seatsFull = full.data.members.length + pending.length >= seats;

  return (
    <Card>
      <CardHeader title="Members" caption={`${full.data.members.length + pending.length} of ${seats} seats used, pending invites included`} />
      <Table label="Members" minWidth={640}>
        <thead><tr><Th>name</Th><Th>role</Th><Th>joined</Th><Th><SrOnly>actions</SrOnly></Th></tr></thead>
        <tbody>
          {full.data.members.map((m) => {
            const editable = manage && m.userId !== me.user.id && canManageMember(workspace.role, m.role);
            return (
              <tr key={m.id}>
                <Td><span className="block font-bold">{m.user.name}{m.userId === me.user.id ? " (you)" : ""}</span><span className="text-xs text-muted">{m.user.email}</span></Td>
                <Td>
                  {editable ? (
                    <Select aria-label={`role of ${m.user.name}`} value={m.role} className="min-h-10 w-36"
                      onChange={(e) => void act.run(() => membersApi.changeRole(workspace.id, m.id, e.target.value as Role))}>
                      {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                    </Select>
                  ) : <Pill tone={m.role === "owner" ? "warn" : "idle"}>{m.role}</Pill>}
                </Td>
                <Td className="text-muted">{formatDate(m.createdAt)}</Td>
                <Td className="text-right">{editable && <Button size="sm" variant="ghost" onClick={() => setRemoving(m)}>remove</Button>}</Td>
              </tr>
            );
          })}
          {pending.map((i) => (
            <tr key={i.id}>
              <Td><span className="block">{i.email}</span><span className="text-xs text-muted">invited · expires {formatDate(i.expiresAt)}</span></Td>
              <Td><Pill tone="info">{i.role}</Pill></Td>
              <Td className="text-muted">pending</Td>
              <Td className="text-right">
                {manage && <Button size="sm" variant="ghost" onClick={() => void act.run(() => membersApi.cancelInvite(i.id))}>cancel invite</Button>}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {act.error && <div className="p-4"><Banner tone="bad">{act.error}</Banner></div>}
      {manage && writable && !seatsFull && <InviteForm workspaceId={workspace.id} roles={roles} onInvited={() => void full.reload()} />}
      {manage && !writable && (
        <p className="border-t-2 border-ink p-5 text-muted">
          // this workspace has no active plan, so it can't invite people. <Link to="/billing">choose a plan</Link> to invite again.
        </p>
      )}
      {manage && writable && seatsFull && (
        <p className="border-t-2 border-ink p-5 text-muted">
          // every seat on this plan is taken. <Link to="/billing">upgrade the plan</Link> to invite more people.
        </p>
      )}

      <ConfirmDialog
        open={!!removing}
        title={`Remove ${removing?.user.name ?? "member"}?`}
        confirmLabel="remove"
        danger
        pending={act.pending}
        onConfirm={() => removing && void act.run(() => membersApi.remove(workspace.id, removing.id)).then(() => setRemoving(null))}
        onCancel={() => setRemoving(null)}
      >
        <p>They lose access to this workspace right away. Their videos and streams stay.</p>
      </ConfirmDialog>
    </Card>
  );
}
